import React from 'react';
import { observer } from 'mobx-react-lite';
import { horizontalListSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import { restrictToHorizontalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { IconButton } from '@producer/ui-kit';

import { StripboardTableHeaderCell } from './stripboard-table-header-cell';
import { Tooltip } from '../../../components/tooltip/Tooltip';
import { uiStore } from '../../../core/stores/ui-store';
import { ControlHubIcon } from '../../../components/icons';

import { context } from '../../../app/context';
import { useCurrentStep } from '../../../hooks/use-current-step';

import styles from '../stripboard.module.scss';

interface StripboardTableHeaderProps {
  totalSize: number | string;
  headerGroups: any[];
  columns: any[];
  onReorderColumn: (propertyId: string, oldIndex: number, newIndex: number) => void;
}

export const StripboardTableHeader: React.FC<StripboardTableHeaderProps> = observer(
  ({ totalSize, headerGroups, columns, onReorderColumn }) => {
    const currentMember = context.member;
    const currentStep = useCurrentStep();
    const view = currentStep.resource;

    const sensors = useSensors(
      useSensor(MouseSensor, {
        activationConstraint: {
          distance: 8,
        },
      }),
      useSensor(TouchSensor, {
        activationConstraint: {
          delay: 200,
          tolerance: 5,
        },
      }),
    );

    const handleDragEnd = (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over) return;

      const oldIndex = columns.findIndex(
        // @ts-ignore
        (col) => col.accessorKey === active.data.current?.column.column.columnDef.accessorKey,
      );

      const newIndex = columns.findIndex(
        // @ts-ignore
        (col) => col.accessorKey === over.data.current?.column.column.columnDef.accessorKey,
      );

      onReorderColumn(active.id.toString(), oldIndex, newIndex);
      return;
    };

    return (
      <div className={styles.tableHeader}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToHorizontalAxis, restrictToParentElement]}
        >
          <SortableContext
            // @ts-ignore
            items={columns?.map((col) => col.accessorKey)}
            strategy={horizontalListSortingStrategy}
          >
            {headerGroups.map((headerGroup) => (
              <div
                key={headerGroup.id}
                className={styles.headerRow}
                style={{
                  width: `max(${totalSize}px, 100%)`,
                }}
              >
                {headerGroup.headers.map((header) => (
                  <StripboardTableHeaderCell key={header.id} header={header} />
                ))}
              </div>
            ))}
          </SortableContext>

          <div className={styles['header-action']}>
            {currentMember && currentMember.role !== 'guest' && (
              <Tooltip text="Customize properties" delay={500}>
                <IconButton
                  icon={<ControlHubIcon />}
                  data-testid="customize-props-button"
                  disabled={!view}
                  onClick={() =>
                    uiStore.isPropertiesPanelOpen
                      ? uiStore.closePropertiesPanel()
                      : uiStore.openPropertiesPanel(view._id)
                  }
                ></IconButton>
              </Tooltip>
            )}
          </div>
        </DndContext>
      </div>
    );
  },
);
